// Copyright (c) StrangeBee 2024

import client from '@Client';
import { useLocalMessage } from '@Helpers/locale';
import useListQuery from '@Hooks/useListQuery';
import useValidateForm from '@Hooks/useValidateForm';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { useListAllFields } from '@Queries/describeQueries';
import React, { ReactElement } from 'react';
import { computeListSelector } from '@Organisms/Task/selector';
import { TaskBulkEditForm, TaskBulkEditValues } from './TaskBulkEditForm';
import { TaskOutput, UserOutput } from 'thehive-sdk';

interface TaskBulkEditManageProps {
    triggerElem: ReactElement;
    onEdit: (values: TaskBulkEditValues) => void;
    caseId: string;
}

export const TaskBulkEditManage = ({ triggerElem, onEdit, caseId }: TaskBulkEditManageProps): ReactElement => {
    const i = useLocalMessage();
    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();
    const { data: describeData } = useListAllFields();

    const { data: taskList = [], refetch: refetchTaskList } = useListQuery<TaskOutput[]>({
        controller: client.TaskController,
        queryParams: {
            selector: computeListSelector({ caseId, describeData: describeData as any }),
        },
        manual: true,
        fetchOnStart: false,
    });

    const { data: assignableUsers = [], refetch: refetchAssignableUsers } = useListQuery<UserOutput[]>({
        queryParams: {
            selector: {
                head: [{ name: 'getCase', idOrName: caseId }, { name: 'assignableUsers' }],
                name: `get-case-${caseId}-assignable-users`,
                initialFilters: [
                    {
                        field: 'locked',
                        operator: 'any',
                        values: [false],
                        type: 'boolean',
                        describeAttribute: describeData?.user.attributes.find(({ name }) => name === 'locked') as any,
                    },
                ],
                sort: ['+name'],
            },
        },
        controller: client.UserController,
        manual: true,
        fetchOnStart: false,
    });

    const onFinish = (values: TaskBulkEditValues): void => {
        const valuesProcessed: any = Object.fromEntries(
            Object.entries(values).filter(([key]) => {
                return !key.includes('Checked') && values[`${key}Checked`];
            }),
        );

        onEdit(valuesProcessed);
        form.resetFields();
    };

    return (
        <THDrawer
            title={i('task.bulk-edit.drawer.title')}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                if (visible) {
                    refetchTaskList();
                    refetchAssignableUsers();
                }
            }}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <TaskBulkEditForm
                    onFinish={(values) => {
                        onFinish(values);
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onCancel={() => {
                        form.resetFields();
                        toggleDrawer();
                        setFieldsTouched(false);
                    }}
                    isValid={isValid && fieldsTouched}
                    taskList={taskList}
                    assignableUsers={assignableUsers}
                />
            )}
        </THDrawer>
    );
};
